<template>
    <v-container>
        <product-quick-detail
            :product="product"
            :add-value="toAdd"
            :is-modal="false"
            :has-action-price="hasActionPrice"
            @selected-variant="variant = $event"
            @to-add="toAdd = $event"
            @intersecting="showFoot = !$event"
        />
        <product-details :product="product" />
        <client-only>
            <teleport to="#footer-teleport" v-if="$vuetify.display.mdAndUp">
                <div v-if="showFoot" style="margin-top: 100px"></div>
                <v-fade-transition>
                <div v-if="showFoot">
                    <div
                        class="position-fixed bg-white"
                        style="bottom: 0; left: 0; right: 0; height: 100px"
                    >
                        <v-container class="d-flex">
                            <div class="align-self-center flex-fill">
                                <div class="d-flex justify-space-between">
                                    <div class="d-flex align-center flex-grow-1">
                                        <v-img
                                            :src="
                                                variant?.main_image?.url || product.main_image?.url
                                            "
                                            width="60"
                                            :alt="
                                                variant?.main_image?.alt || product.main_image?.alt
                                            "
                                            class="flex-grow-0"
                                            aspect-ratio="1"
                                        />
                                        <div
                                            class="d-flex justify-space-between flex-wrap flex-grow-1 mx-3 align-center"
                                            style="gap: 0.5em"
                                        >
                                            <span class="text-h6">{{
                                                variant?.name || product.name
                                            }}</span>
                                            <template v-if="variant">
                                                <template v-if="hasActionPrice">
                                                    <div class="d-flex flex-column">
                                                        <div class="to-warning__static text-h5">
                                                            {{ variant.price }}
                                                        </div>
                                                        <div
                                                            class="to-warning__static text-subtitle-1 text-decoration-line-through"
                                                        >
                                                            {{ variant.base_price }}
                                                        </div>
                                                    </div>
                                                </template>
                                                <template v-else>
                                                    <div class="to-warning__static text-h5">
                                                        {{ variant.price }}
                                                    </div>
                                                </template>
                                            </template>
                                            <template v-else>
                                                <template v-if="hasActionPrice">
                                                    <div class="d-flex flex-column">
                                                        <div class="to-warning__static text-h5">
                                                            {{ product.price }}
                                                        </div>
                                                        <div
                                                            class="to-warning__static text-subtitle-1 text-decoration-line-through"
                                                        >
                                                            {{ product.base_price }}
                                                        </div>
                                                    </div>
                                                </template>
                                                <template v-else>
                                                    <div class="to-warning__static text-h5">
                                                        {{ product.price }}
                                                    </div>
                                                </template>
                                            </template>
                                        </div>
                                    </div>
                                    <div v-if="!product.info_product" class="d-flex align-center">
                                        <quantity-changer
                                            v-model="toAdd"
                                            :stock="
                                                !!variant
                                                    ? variant.on_stock
                                                    : product.variants.length
                                                      ? 0
                                                      : product.on_stock
                                            "
                                            :disabled="disabled"
                                            class="bottom-changer mr-3"
                                        />
                                        <v-btn
                                            v-if="$page.props.eshopEnabled"
                                            variant="outlined"
                                            :disabled="disabled"
                                            class="cart-btn px-8 cart-btn__border hovering to-warning__static"
                                            color="cis-accent-color"
                                            rounded="0"
                                            @click="addToOrder(toAdd)"
                                        >
                                            <div class="inline-flex align-center">
                                                <v-icon class="mr-3" :icon="mdiCart" />
                                                <span>{{
                                                        $t('eshop::product-page.add_to_cart')
                                                    }}</span>
                                            </div>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </v-container>
                    </div>
                </div>
                </v-fade-transition>
            </teleport>
        </client-only>
    </v-container>
</template>

<script setup>
import ProductQuickDetail from '../ProductQuickDetail.vue';
import { computed, inject, ref } from 'vue';
import { usePage, router } from '@inertiajs/vue3';
import QuantityChanger from '../QuantityChanger.vue';
import ProductDetails from '../ProductDetails.vue';
import { mdiCart } from '@mdi/js';
import { addEvent, generateItem } from '../../../../gtm';
import ClientOnly from '../../../../frontend/components/ClientOnly.vue';
import { useCartStore } from '../../store/cart';
import pinia from '../../../../plugins/pinia';

const route = inject('route');

const cartStore = useCartStore(pinia);

const product = computed(() => usePage().props.product);
const variant = ref(null);
const toAdd = ref(0);
const showFoot = ref(false);

const hasActionPrice = computed(() => {
    let has = false;
    let base, action;
    if (variant.value) {
        base = parseFloat(variant.value.base_price.replace(/ /g, ''));
        action = parseFloat(variant.value.price.replace(/ /g, ''));
    } else {
        base = parseFloat(product.value.base_price.replace(/ /g, ''));
        action = parseFloat(product.value.price.replace(/ /g, ''));
    }
    if (action < base) {
        has = true;
    }
    return has;
});

const disabled = computed(() => {
    if (Array.isArray(product.value.variants)) {
        if (!product.value.variants.length) {
            return false;
        }
    }
    if (variant.value?.id) {
        return false;
    }
    return true;
});

const addToOrder = (count = 1) => {
    // eslint-disable-next-line no-undef
    router.post(
        route('order.addProduct'),
        { id_product: product.value.id, count, id_variant: variant.value?.id },
        {
            onSuccess: () => {
                const item = cartStore.cartItems.find((item) => {
                    if (variant.value?.id) {
                        return (
                            item.id_products == product.value.id &&
                            item.id_products_variants == variant.value.id
                        );
                    } else {
                        return item.id_products == product.value.id;
                    }
                });
                if (item) {
                    const price = parseFloat(item.nice_price.replace(/\s/g, '').replace(/,/g, '.'));
                    const attributes = {
                        quantity: count,
                        price: price.toFixed(2),
                    };
                    if (variant.value?.id) {
                        attributes.item_variant = variant.value.id;
                    }
                    addEvent({
                        event: 'add_to_cart',
                        ecommerce: {
                            currency: usePage().props.currency,
                            value: (price * count).toFixed(2),
                            items: generateItem(product.value, attributes),
                        },
                    });
                }
            },
        }
    );
};
</script>

<style scoped lang="scss">
::v-deep(.bottom-changer) {
    .v-btn--stacked {
        --v-btn-height: 48px;
    }
}

.cart-btn__border {
    &.v-btn--stacked {
        --v-btn-height: 50px;
    }
}
</style>
